import React, { useState } from 'react';
// no-op
import { format, parseISO, isValid } from 'date-fns';
import { SocialIcon } from '../icons/SocialIcon';
import type { Post } from '../../types/calendar';
import type { SocialPlatform } from '../icons/SocialIcon';
import { MediaDisplay } from '../posts/previews/MediaDisplay';
import { PdfThumb } from '../common/PdfThumb';
import { PostPreview } from './PostPreview';
import { normalizeMedia } from '../../utils/mediaUtils';

// Helper function to get platforms as array. usePosts normalizes the
// backend object format into a string[]; older call sites still pass the
// raw object. Accept both.
const getPlatformsArray = (post: any): string[] => {
  if (!post.platforms) return [];
  if (Array.isArray(post.platforms)) return post.platforms.filter((p: any): p is string => typeof p === 'string');
  if (typeof post.platforms === 'object') return Object.keys(post.platforms);
  return [];
};

interface CalendarPostProps {
  post: Post;
  onDragStart?: (e: React.DragEvent, post: Post) => void;
}

export const CalendarPost: React.FC<CalendarPostProps> = ({ post, onDragStart }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const getOptimalPosition = () => {
    // Dynamically size the preview card so it never exceeds 90% of the viewport on mobile
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const cardWidth = Math.min(800, viewportWidth * 0.9);
    const cardHeight = Math.min(500, viewportHeight * 0.8);
    const margin = 20; // Margin from screen edges

    let x = mousePosition.x + 10; // Default: to the right of cursor
    let y = mousePosition.y - 100; // Default: above cursor

    // Prevent right overflow
    if (x + cardWidth + margin > viewportWidth) {
      x = mousePosition.x - cardWidth - 10;
    }

    // Prevent left overflow
    if (x < margin) {
      x = margin;
    }

    // Prevent bottom overflow
    if (y + cardHeight + margin > viewportHeight) {
      y = viewportHeight - cardHeight - margin;
    }

    // Prevent top overflow
    if (y < margin) {
      y = margin;
    }

    return { x, y };
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const getFormattedTime = () => {
    // Ideas don't show time
    if (post.status === 'idea') return '';
    // Try scheduledAt first, then fallback to updatedAt (to match WeekView logic)
    const dateString = post.scheduledAt || post.updatedAt;
    
    if (dateString) {
      try {
        const date = parseISO(dateString);
        if (isValid(date)) {
          return format(date, 'HH:mm');
        }
      } catch {
        // Fallback to new Date if parseISO fails
        try {
          const date = new Date(dateString);
          if (isValid(date)) {
            return format(date, 'HH:mm');
          }
        } catch {
          return '';
        }
      }
    }
    return '';
  };

  // Debug: log data
  console.log('Post data:', { 
    content: post.content.substring(0, 20) + '...',
    scheduledAt: post.scheduledAt,
    formattedTime: getFormattedTime(),
    status: post.status
  });

  const getStatusEmoji = (status: Post['status'] | 'waiting_for_approval') => {
    switch (status) {
      case 'idea':
        return '💡';
      case 'draft':
        return '📝';
      case 'scheduled':
        return '📆';
      case 'pending':
        return '🚧';
      case 'waiting_for_approval':
        return '⏳';
      case 'published':
        return '✅';
      default:
        return '';
    }
  };

  // Deprecated icon helper (replaced by unified thumbnail components)

  const getMediaFormatEmoji = (post: Post) => {
    // Determine media format based on post properties
    const normalizedMedia = normalizeMedia(post.media);
    if (!normalizedMedia) {
      return '📝'; // Text only
    }
    
    if (normalizedMedia.type === 'video') {
      return '🎬'; // Video
    }
    
    if (normalizedMedia.type === 'image') {
      return '🖼️'; // Image
    }
    
    return '📱'; // Default fallback
  };

  return (
    <>
      <div 
        draggable={true}
        onDragStart={(e) => onDragStart?.(e, post)}
        className={`p-0.5 sm:p-1 border rounded text-xs cursor-move w-full max-w-full overflow-hidden relative ${
          post.status === 'idea' ? 'bg-amber-50 border-amber-400 border-dashed hover:bg-amber-100' :
          post.status === 'ready_to_publish' ? 'bg-white border-green-500 hover:bg-gray-50' :
          post.status === 'rejected' ? 'bg-white border-red-500 hover:bg-gray-50' :
          post.status === 'waiting_for_approval' ? 'bg-white border-orange-500 hover:bg-gray-50' :
          'bg-white border-black hover:bg-gray-50'
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex items-start gap-1 sm:gap-2 w-full">
          {(() => {
            const normalizedMedia = normalizeMedia(post.media);
            if (!normalizedMedia) return null;
            const isPdf = /\.pdf(\?|#|$)/i.test(normalizedMedia.url);
            return (
              <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-gray-100 rounded overflow-hidden relative">
                {isPdf ? (
                  <PdfThumb src={normalizedMedia.url} className="w-full h-full" frame="square" />
                ) : (
                  <MediaDisplay
                    mediaUrl={normalizedMedia.url}
                    altText="Post media"
                    type={normalizedMedia.type}
                    className="w-full h-full"
                    objectFit="cover"
                    controls={false}
                    autoPlayOnMount={normalizedMedia.type === 'video'}
                    muted
                  />
                )}
              </div>
            );
          })()}
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-start justify-between gap-0.5 sm:gap-1">
              <div className="flex-1 min-w-0 overflow-hidden">
                <span className="block truncate text-[10px] sm:text-xs leading-tight">{post.status === 'idea' ? (post.title || post.content) : post.content}</span>
                {!normalizeMedia(post.media) && (
                  <span className="text-[8px] sm:text-[10px] bg-gray-100 px-0.5 py-0 rounded mt-0.5 inline-block">{getMediaFormatEmoji(post)}</span>
                )}
              </div>
              <span className="text-[10px] sm:text-xs flex-shrink-0">{getStatusEmoji(post.status)}</span>
            </div>
            <div className="flex items-center justify-between mt-0.5 sm:mt-1">
              <div className="flex gap-0.5 items-center min-w-0 overflow-hidden">
                {post.brandLogo && (
                  <img 
                    src={post.brandLogo} 
                    alt={post.brandName || 'Brand logo'}
                    className="w-2 h-2 sm:w-3 sm:h-3 rounded object-cover flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                {getPlatformsArray(post).map((platform) => (
                  <SocialIcon 
                    key={platform}
                    platform={platform as SocialPlatform}
                    size={10}
                  />
                ))}
              </div>
              {getFormattedTime() && (
                <span className="text-[10px] sm:text-xs text-gray-500 flex-shrink-0 ml-1">{getFormattedTime()}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {isHovered && (
        <div 
          className="fixed pointer-events-none"
          style={{
            left: getOptimalPosition().x,
            top: getOptimalPosition().y,
            zIndex: 1000
          }}
        >
          <PostPreview post={post} />
        </div>
      )}
    </>
  );
};